import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Blade from '@/components/ui/Blade/Blade';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Fund from '@/models/Fund';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';
import detailsIcon from '@/assets/icons/id2.svg';
import permissionsIcon from '@/assets/icons/permissions.svg';
import fundsIcon from '@/assets/icons/bank.svg';
import activityIcon from '@/assets/icons/time.svg';
import editIcon from '@/assets/icons/edit-pencil.svg';
import loginAsIcon from '@/assets/icons/login-as.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import RoleChips from './RoleChips';
import { formatUserDate, getUserFullName } from './userHelpers';
import styles from './UserBlade.module.css';

interface UserBladeProps {
  user: ManagedUserDtoIn;
  onClose: () => void;
  /** Actions are shown to admins only - the matching endpoints are admin-only too. */
  canManage: boolean;
  isSelf: boolean;
  onEditDetails: () => void;
  onEditPermissions: () => void;
  onLoginAs: () => void;
  onDelete: () => void;
}

const UserBlade = ({ user, onClose, canManage, isSelf, onEditDetails, onEditPermissions, onLoginAs, onDelete }: UserBladeProps) => {
  const { t } = useTranslation();
  const { data: funds } = useGetAuthApi<Fund[]>('funds');

  const userFunds = useMemo(() => {
    const namesById = new Map((funds ?? []).map((fund) => [fund.id, fund.name]));
    return (user.fundIds ?? []).map((id) => ({ id, name: namesById.get(id) ?? '' }));
  }, [funds, user.fundIds]);

  const section = (icon: string, title: string, children: ReactNode) => (
    <div>
      <h2 className={styles.sectionTitle}>
        <TintedIcon icon={icon} size={17} />
        {title}
      </h2>
      {children}
    </div>
  );

  const detail = (label: string, value?: string | null, dir?: 'ltr') => (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      {value ? (
        <span className={styles.detailValue} dir={dir}>
          {value}
        </span>
      ) : (
        <span className={`${styles.detailValue} ${styles.detailValueMuted}`}>-</span>
      )}
    </div>
  );

  const activityRow = (label: string, value?: string) => {
    const formatted = formatUserDate(value);
    return formatted ? detail(label, formatted) : detail(label, t('usersManagement.never'));
  };

  const headerTop = (
    <div className={styles.headerName}>
      <span className={styles.headerTitle}>{getUserFullName(user) || t('usersManagement.noName')}</span>
      <span className={styles.headerEmail} dir="ltr">
        {user.email}
      </span>
    </div>
  );

  return (
    <Blade handleXclick={onClose} headerTop={headerTop} headerBottom={<></>} width="460px">
      <div className={styles.content}>
        {canManage && (
          <div className={styles.actionsRow}>
            <button type="button" className={styles.pill} onClick={onEditDetails}>
              <TintedIcon icon={editIcon} size={16} />
              {t('usersManagement.actions.edit')}
            </button>
            <button type="button" className={styles.pill} onClick={onEditPermissions}>
              <TintedIcon icon={permissionsIcon} size={16} />
              {t('usersManagement.actions.permissions')}
            </button>
            <button
              type="button"
              className={styles.pill}
              onClick={onLoginAs}
              disabled={isSelf}
              title={isSelf ? t('usersManagement.loginAs.selfDisabled') : undefined}
            >
              <TintedIcon icon={loginAsIcon} size={16} />
              {t('usersManagement.actions.loginAs')}
            </button>
            <button
              type="button"
              className={styles.pill}
              onClick={onDelete}
              disabled={isSelf}
              title={isSelf ? t('usersManagement.delete.selfDisabled') : undefined}
            >
              <TintedIcon icon={deleteIcon} size={16} />
              {t('usersManagement.actions.delete')}
            </button>
          </div>
        )}

        {section(
          detailsIcon,
          t('usersManagement.blade.details'),
          <div className={styles.detailsList}>
            {detail(t('email'), user.email, 'ltr')}
            {detail(t('firstName'), user.firstName)}
            {detail(t('lastName'), user.lastName)}
            {detail(t('phoneNumber'), user.phoneNumber, 'ltr')}
          </div>
        )}

        {section(permissionsIcon, t('usersManagement.columns.roles'), <RoleChips roles={user.roles ?? []} />)}

        {section(
          fundsIcon,
          t('usersManagement.blade.funds', { count: userFunds.length }),
          userFunds.length === 0 ? (
            <p className={styles.emptyText}>{t('usersManagement.blade.noFunds')}</p>
          ) : (
            <div className={styles.fundList}>
              {userFunds.map((fund) => (
                <div key={fund.id} className={styles.fundRow}>
                  <span className={styles.fundId}>{fund.id}</span>
                  <span className={styles.fundName}>{fund.name || t('usersManagement.blade.unknownFund')}</span>
                </div>
              ))}
            </div>
          )
        )}

        {section(
          activityIcon,
          t('usersManagement.blade.activity'),
          <div className={styles.detailsList}>
            {activityRow(t('usersManagement.columns.lastLogin'), user.lastSuccessfulLoginTime)}
            {activityRow(t('usersManagement.columns.lastSeen'), user.lastSuccessfulTokenValidationTime)}
            {activityRow(t('usersManagement.columns.registeredAt'), user.creationTime)}
            {activityRow(t('usersManagement.columns.lastModified'), user.lastModifiedTime)}
            {activityRow(t('usersManagement.blade.lastFailedLogin'), user.lastFailedLoginAttempt)}
          </div>
        )}
      </div>
    </Blade>
  );
};

export default UserBlade;
